import { Search } from "lucide-react";
import { useEffect, useState, useCallback } from "react";

import { Footer } from "@/components/Footer";
import { OwnerCard } from "@/components/owner/OwnerCard";
import { CreateOwnerDialog } from "@/components/owner/OwnerCreateDialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Sidebar } from "@/components/Sidebar";

interface Owner {
  id: number;
  name: string;
  telephone: string;
  cell_phone: string;
  email: string;
  observation: string;
}

interface OwnerData {
  id: number;
  attributes: {
    name: string;
    telephone: string;
    cell_phone: string;
    email: string;
    observation: string;
  };
}

interface ApiResponse {
  data: OwnerData[];
  meta: {
    pagination: {
      page: number;
      pageSize: number;
      pageCount: number;
      total: number;
    };
  };
}

export function OwnerControl() {
  const [owners, setOwners] = useState<Owner[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState<"name" | "email">("name");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const isAdmin = true;

  const itemsPerPage = 10;
  const route = import.meta.env.VITE_URL_ENDPOINT;

  const buildQuery = useCallback(() => {
    const params = new URLSearchParams();
    params.append("pagination[page]", String(currentPage));
    params.append("pagination[pageSize]", String(itemsPerPage));
    if (searchTerm.trim()) {
      const field = filterType === "name" ? "name" : "email";
      params.append(`filters[${field}][$containsi]`, searchTerm.trim());
    }
    return params.toString();
  }, [currentPage, itemsPerPage, searchTerm, filterType]);

  const fetchOwners = useCallback(async () => {
    try {
      const qs = buildQuery();
      const res = await fetch(`${route}/api/owners?${qs}`);
      if (!res.ok) throw new Error("Erro na resposta da API");
      const json: ApiResponse = await res.json();
      const mapped = json.data.map((o) => ({
        id: o.id,
        name: o.attributes.name,
        telephone: o.attributes.telephone,
        cell_phone: o.attributes.cell_phone,
        email: o.attributes.email,
        observation: o.attributes.observation,
      }));
      setOwners(mapped);
      setTotalPages(json.meta.pagination.pageCount);
    } catch (err) {
      console.error("Não foi possível buscar proprietários:", err);
      setOwners([]);
    }
  }, [buildQuery, route]);

  useEffect(() => {
    fetchOwners();
  }, [fetchOwners]);

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="flex min-h-screen">
        <Sidebar
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          isAdmin={isAdmin}
        />

        {/* Overlay for mobile */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 z-40 bg-black/50 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main Content */}
        <div className="flex flex-1 flex-col lg:ml-0">
          {/* Top Header */}
          <header className="border-b border-gray-200 bg-white shadow-sm">
            <div className="flex h-16 items-center justify-between px-4 sm:px-6 lg:px-8">
              <div className="flex items-center">
                <button
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                  className="rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 lg:hidden"
                >
                  <i className="fas fa-bars text-xl"></i>
                </button>
                <h1 className="ml-4 text-xl font-semibold text-gray-900 sm:text-2xl lg:ml-0">
                  Gerenciamento de Proprietários
                </h1>
              </div>
            </div>
          </header>

          {/* Filtros e conteúdo */}
          <div className="flex-1 p-8">
            <div className="mx-auto max-w-4xl">
              <h1 className="mb-3 text-center text-2xl font-bold">
                Lista de Proprietários
              </h1>
              {/* Filtros */}
              <div className="mb-6 flex flex-col space-y-4">
                <div className="flex space-x-4">
                  <div className="relative flex-grow">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                      <Search className="text-gray-400" />
                    </span>
                    <Input
                      value={searchTerm}
                      onChange={onSearchChange}
                      className="w-full pl-10"
                      placeholder={`Buscar por ${
                        filterType === "name" ? "nome" : "email"
                      } do proprietário`}
                    />
                  </div>
                  <Select
                    value={filterType}
                    onValueChange={(v: "name" | "email") => {
                      setFilterType(v);
                      setCurrentPage(1);
                    }}
                  >
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Filtrar por" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="name">Nome</SelectItem>
                      <SelectItem value="email">Email</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <CreateOwnerDialog onOwnerCreate={fetchOwners} />
              </div>
              {/* Lista de proprietários */}
              <div className="mt-4 space-y-4">
                {owners.length > 0 ? (
                  owners.map((owner) => (
                    <OwnerCard
                      key={owner.id}
                      id={owner.id}
                      name={owner.name}
                      telephone={owner.telephone}
                      cell_phone={owner.cell_phone}
                      email={owner.email}
                      observation={owner.observation}
                      onOwnerUpdate={fetchOwners}
                    />
                  ))
                ) : (
                  <h2 className="mt-10 text-center">
                    Nenhum proprietário encontrado.
                  </h2>
                )}
              </div>

              {totalPages > 1 && (
                <div className="mt-8 flex flex-col items-center space-y-4 sm:flex-row sm:justify-center sm:space-x-2 sm:space-y-0">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                    disabled={currentPage === 1}
                    className="flex w-full items-center justify-center space-x-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
                  >
                    <i className="fas fa-chevron-left"></i>
                    <span>Anterior</span>
                  </button>

                  <div className="flex space-x-1">
                    {(() => {
                      const pages = [];
                      const maxVisiblePages = 5;
                      const halfVisible = Math.floor(maxVisiblePages / 2);

                      let startPage = Math.max(1, currentPage - halfVisible);
                      let endPage = Math.min(
                        totalPages,
                        startPage + maxVisiblePages - 1,
                      );

                      if (endPage - startPage + 1 < maxVisiblePages) {
                        startPage = Math.max(1, endPage - maxVisiblePages + 1);
                      }

                      if (startPage > 1) {
                        pages.push(
                          <button
                            key={1}
                            onClick={() => setCurrentPage(1)}
                            className="h-10 w-10 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
                          >
                            1
                          </button>,
                        );
                        if (startPage > 2) {
                          pages.push(
                            <span
                              key="dots1"
                              className="flex h-10 w-10 items-center justify-center text-gray-500"
                            >
                              ...
                            </span>,
                          );
                        }
                      }

                      for (let i = startPage; i <= endPage; i++) {
                        pages.push(
                          <button
                            key={i}
                            onClick={() => setCurrentPage(i)}
                            className={`h-10 w-10 rounded-lg text-sm font-medium ${
                              currentPage === i
                                ? "bg-blue-600 text-white"
                                : "border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                            }`}
                          >
                            {i}
                          </button>,
                        );
                      }

                      if (endPage < totalPages) {
                        if (endPage < totalPages - 1) {
                          pages.push(
                            <span
                              key="dots2"
                              className="flex h-10 w-10 items-center justify-center text-gray-500"
                            >
                              ...
                            </span>,
                          );
                        }
                        pages.push(
                          <button
                            key={totalPages}
                            onClick={() => setCurrentPage(totalPages)}
                            className="h-10 w-10 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
                          >
                            {totalPages}
                          </button>,
                        );
                      }

                      return pages;
                    })()}
                  </div>

                  <button
                    onClick={() =>
                      setCurrentPage((p) => Math.min(p + 1, totalPages))
                    }
                    disabled={currentPage === totalPages}
                    className="flex w-full items-center justify-center space-x-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
                  >
                    <span>Próxima</span>
                    <i className="fas fa-chevron-right"></i>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
